import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';

const problems = [
  ['Inloggning och data', 'Firebase, autentisering, användarroller eller dataflöden är inte färdiga eller fungerar bara delvis.'],
  ['Formulär och e-post', 'Kontakt-, boknings- eller offertformulär skickar inget, sparar fel eller saknar tydlig återkoppling.'],
  ['Mobil och gränssnitt', 'Sidan ser bra ut i en vy men går sönder på telefon, mindre skärmar eller i verkliga användarflöden.'],
  ['Domän och publicering', 'Vercel, miljövariabler, DNS, domän eller externa tjänster är inte korrekt kopplade.'],
  ['AI-ändringar har skapat fel', 'Nya instruktioner har löst ett problem men samtidigt brutit andra delar av projektet.'],
  ['Projektet saknar en första version', 'Idén har vuxit för mycket och behöver avgränsas till något som faktiskt kan lanseras och testas.'],
];

const steps = [
  ['01', 'Skicka det som finns', 'Skicka den publicerade länken, en kort beskrivning och vad som måste fungera när uppdraget är klart. Kod eller kontotillgång behövs först när uppdraget har bedömts.'],
  ['02', 'Få en teknisk bedömning', 'Det kontrolleras om projektet går att fortsätta på ett rimligt sätt, vilka delar som blockerar lansering och vad som bör vänta.'],
  ['03', 'Godkänn en avgränsad leverans', 'Du får ett skriftligt förslag med funktioner, begränsningar och pris. Arbetet börjar inte förrän upplägget är godkänt.'],
  ['04', 'Testa och publicera', 'Den överenskomna delen byggs klart, testas och publiceras eller lämnas över med de konton och länkar som ingår.'],
];

const canHelp = [
  'Lovable-, Cursor-, ChatGPT- och andra AI-assisterade webbprojekt',
  'React-, Next.js- och Firebase-projekt med tydligt avgränsade problem',
  'Inloggning, formulär, databaser, adminflöden och mobilanpassning',
  'Vercel, domänkoppling, miljövariabler och publicering',
  'Att minska ett för stort projekt till en lanserbar första version',
];

const notSmallProject = [
  'Bank-, betalnings-, vård- eller andra säkerhetskritiska system',
  'Projekt där ingen äger konton, kod eller rättigheter till materialet',
  'Stora plattformar som förväntas bli helt färdiga inom en liten budget',
  'Projekt som kräver löften om försäljning, investerare eller användartillväxt',
];

export default function AIProjectHelp() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Hjälp med AI-byggda webbprojekt',
    provider: {
      '@type': 'ProfessionalService',
      name: 'StartaWebb',
      url: 'https://startawebb.se',
    },
    areaServed: 'Sweden',
    serviceType: 'Reparation och slutförande av webbprojekt byggda med Lovable, Cursor, ChatGPT och andra AI-verktyg',
  };

  return (
    <>
      <SEOHead
        title="Fixa eller slutför ett Lovable- eller AI-byggt projekt | StartaWebb"
        description="Har ett projekt byggt med Lovable, Cursor, ChatGPT eller andra AI-verktyg fastnat? StartaWebb hjälper med Firebase, inloggning, formulär, mobilvisning, Vercel, domän och lansering."
        canonical="https://startawebb.se/ai-projekt"
        keywords="fixa Lovable projekt, hjälp med Cursor projekt, slutföra AI byggd hemsida, Firebase hjälp, Vercel problem, AI webbutveckling Sverige"
        structuredData={structuredData}
      />

      <section className="relative overflow-hidden bg-slate-950 py-20 text-white md:py-28">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-sky-500/15 blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 lg:grid-cols-[1.08fr_0.92fr]">
          <div>
            <p className="mb-5 inline-flex rounded-full border border-violet-400/30 bg-violet-400/10 px-4 py-2 text-sm font-bold text-violet-200">
              Lovable · Cursor · ChatGPT · AI-assisterad utveckling
            </p>
            <h1 className="mb-6 text-4xl font-extrabold leading-[1.08] tracking-tight md:text-6xl">
              Har projektet fastnat precis innan det skulle bli klart?
            </h1>
            <p className="mb-8 max-w-3xl text-lg leading-relaxed text-slate-300 md:text-xl">
              StartaWebb hjälper till att bedöma, reparera och slutföra avgränsade webbprojekt som har byggts med AI-verktyg men fortfarande saknar fungerande teknik, mobilanpassning eller publicering.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/contact?behov=ai-projekt" className="rounded-xl bg-violet-500 px-7 py-4 text-center font-bold text-white shadow-lg shadow-violet-950/30 transition hover:bg-violet-400">
                Skicka projektlänken
              </Link>
              <a href="#sa-fungerar-det" className="rounded-xl border border-white/20 px-7 py-4 text-center font-bold text-white transition hover:bg-white/10">
                Se hur det går till
              </a>
            </div>
          </div>

          <aside className="rounded-3xl border border-white/10 bg-white/5 p-7 shadow-2xl backdrop-blur md:p-9">
            <p className="mb-4 text-sm font-bold uppercase tracking-[0.14em] text-violet-300">Det räcker att börja med</p>
            <div className="space-y-4">
              {[
                'Länken till den publicerade sidan eller förhandsvisningen',
                'Vad som fungerar i dag och vad som är trasigt',
                'De viktigaste funktionerna som måste bli klara',
                'Vilka verktyg eller tjänster projektet använder, om du vet',
              ].map((item) => (
                <div key={item} className="flex gap-3 rounded-2xl bg-white/5 p-4 text-slate-200">
                  <span className="font-bold text-violet-300">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm leading-relaxed text-slate-400">Du behöver inte kunna förklara problemet tekniskt. Beskriv vad användaren ska kunna göra när lösningen fungerar.</p>
          </aside>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 font-semibold text-violet-700">Vanliga problem</p>
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">Nästan färdigt är inte samma sak som lanserbart.</h2>
            <p className="text-lg leading-relaxed text-slate-600">AI kan skapa mycket snabbt, men de sista delarna kräver ofta att någon förstår hur hela flödet hänger ihop och kontrollerar att det fungerar utanför den första demon.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {problems.map(([title, text], index) => (
              <article key={title} className="rounded-2xl border border-slate-200 bg-slate-50 p-7">
                <span className="mb-5 flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 font-extrabold text-violet-800">{String(index + 1).padStart(2, '0')}</span>
                <h3 className="mb-3 text-xl font-bold text-slate-950">{title}</h3>
                <p className="leading-relaxed text-slate-600">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="sa-fungerar-det" className="scroll-mt-24 bg-sky-50 py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="mb-3 font-semibold text-sky-700">Så fungerar det</p>
            <h2 className="text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">Ingen öppen beställning utan gräns.</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {steps.map(([number, title, text]) => (
              <article key={number} className="rounded-2xl border border-sky-100 bg-white p-7 shadow-sm">
                <p className="mb-5 text-sm font-extrabold tracking-[0.18em] text-sky-600">{number}</p>
                <h3 className="mb-3 text-xl font-bold text-slate-950">{title}</h3>
                <p className="leading-relaxed text-slate-600">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 lg:grid-cols-2">
          <article className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 md:p-10">
            <p className="mb-3 font-semibold text-emerald-800">Passar ofta</p>
            <h2 className="mb-6 text-3xl font-bold text-slate-950">Avgränsade projekt med ett tydligt stopp.</h2>
            <div className="space-y-4 text-slate-700">
              {canHelp.map((item) => (
                <p key={item} className="flex gap-3"><span className="font-bold text-emerald-700">✓</span><span>{item}</span></p>
              ))}
            </div>
          </article>
          <article className="rounded-3xl border border-amber-200 bg-amber-50 p-8 md:p-10">
            <p className="mb-3 font-semibold text-amber-800">Kräver annan lösning eller större team</p>
            <h2 className="mb-6 text-3xl font-bold text-slate-950">Alla projekt ska inte räddas som små uppdrag.</h2>
            <div className="space-y-4 text-slate-700">
              {notSmallProject.map((item) => (
                <p key={item} className="flex gap-3"><span className="font-bold text-amber-700">•</span><span>{item}</span></p>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="mb-3 font-semibold text-sky-700">Relevant erfarenhet</p>
            <h2 className="mb-5 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">Projekt med databaser, användarflöden och publicering.</h2>
            <p className="mb-6 text-lg leading-relaxed text-slate-600">BokaNära och BolagsAnnons visar arbete med inloggning, Firebase, formulär, adminpaneler, sökning, bokning och publicerade webbflöden.</p>
            <Link to="/portfolio" className="font-bold text-sky-700 hover:text-sky-900">Se projekten →</Link>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            {[
              ['BokaNära', '/project-screenshots/bokanara-home.webp', 'https://bokanara.se'],
              ['BolagsAnnons', '/project-screenshots/bolagsannons-home.webp', 'https://bolagsannons.se'],
            ].map(([name, image, url]) => (
              <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <img src={image} alt={`Skärmbild från ${name}`} className="aspect-[1200/620] w-full rounded-xl object-cover object-top" loading="lazy" />
                <p className="px-2 pb-1 pt-4 font-bold text-slate-950">{name} ↗</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-5xl items-center gap-8 rounded-3xl bg-violet-700 px-8 py-10 text-white shadow-xl md:px-12 lg:grid-cols-[1fr_auto]">
          <div>
            <p className="mb-3 font-semibold text-violet-100">Skicka det du har</p>
            <h2 className="mb-4 text-3xl font-bold tracking-tight md:text-4xl">Få besked om projektet går att slutföra på ett rimligt sätt.</h2>
            <p className="max-w-2xl text-lg leading-relaxed text-violet-50">Börja med länken och det viktigaste som inte fungerar. Ett tydligt pris lämnas först när den första leveransen går att avgränsa.</p>
          </div>
          <Link to="/contact?behov=ai-projekt" className="min-w-64 rounded-xl bg-white px-7 py-4 text-center font-bold text-violet-800 transition hover:bg-violet-50">
            Skicka projektlänken
          </Link>
        </div>
      </section>
    </>
  );
}
